import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { urlParamReplicaId, urlImportDocuments, urlImportDocumentsUpdate } from '../../Constants';
import { IAction, SlicePropsWithData } from '../store';
import { IDocDefaultFields } from '../project/documents/sliceProjectDocuments';
import { toBeginUpperCase } from '../../util/StringUtils';
import { ICategorizationSuccess } from '../general/sliceCategorization';
import { apiFetch } from '../../util/ProjectUtils';

export interface IImportDocumentsState extends SlicePropsWithData<IDocDefaultFields[]> {
    categorizationState: null | 'pending' | ICategorizationSuccess;
}

const initialState: IImportDocumentsState = {
    data: [],
    loading: false,
    error: null,
    categorizationState: null,
};

const reducers = {
    setDocuments(
        state: SlicePropsWithData<IDocDefaultFields[]>,
        action: IAction<IDocDefaultFields[]>,
    ) {
        state.data = action.payload;
    },
    resetCategorizationState(state: IImportDocumentsState) {
        state.categorizationState = null;
    },
};

const thunks = {
    getImportDocuments: async (config: {
        replicaId: string;
        fields: string[];
    }): Promise<IDocDefaultFields[]> => {
        try {
            const { replicaId } = config;
            let { fields } = config;

            // 7114: ensure fields below for categorization functionality
            fields = Array.from(
                new Set([...fields, 'CategoryID', 'PackageID', 'KeywordID', 'AddressID']),
            );

            const url = urlImportDocuments.replace(urlParamReplicaId, replicaId);
            const request: RequestInit = {
                method: 'GET',
                headers: {
                    fields: fields.join(';'),
                },
            };

            const response = await apiFetch(url, request);

            if (!response.ok) {
                return Promise.reject(new Error(await response.text()));
            }

            const docs = await response.json();

            return Promise.resolve(docs);
        } catch (e) {
            console.error(e);
            return Promise.reject(e as Error);
        }
    },

    updateImportDocuments: async (config: {
        replicaId: string;
        viewName: string;
        docsJson: string;
        isCategorization?: boolean;
        count?: number;
    }): Promise<object> => {
        try {
            const { replicaId, viewName, docsJson } = config;

            const url = urlImportDocumentsUpdate.replace(urlParamReplicaId, replicaId);

            const formData = new FormData();
            formData.append('docsJson', docsJson);

            const request: RequestInit = {
                method: 'PUT',
                headers: {
                    viewName: toBeginUpperCase(viewName),
                },
                body: formData,
            };

            const response = await apiFetch(url, request);

            if (!response.ok) {
                return Promise.reject(new Error(await response.text()));
            }

            const resultJson = await response.json();

            return Promise.resolve(resultJson);
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    _reducers: (builder: ActionReducerMapBuilder<IImportDocumentsState>) => {
        builder.addCase(getImportDocuments.pending, (state: Draft<IImportDocumentsState>) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(
            getImportDocuments.fulfilled,
            (state: Draft<IImportDocumentsState>, action: IAction<IDocDefaultFields[]>) => {
                state.loading = false;
                state.error = null;
                state.data = action.payload;
            },
        );
        builder.addCase(
            getImportDocuments.rejected,
            (state: Draft<IImportDocumentsState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
                state.data = [];
            },
        );

        builder.addCase(
            updateImportDocuments.pending,
            (state: Draft<IImportDocumentsState>, action) => {
                if (action.meta.arg.isCategorization) {
                    state.categorizationState = 'pending';
                }
            },
        );
        builder.addCase(
            updateImportDocuments.fulfilled,
            (state: Draft<IImportDocumentsState>, action) => {
                if (action.meta.arg.isCategorization) {
                    state.categorizationState = {
                        docCount: action.meta.arg.count,
                    } as ICategorizationSuccess;
                }
            },
        );
        builder.addCase(updateImportDocuments.rejected, (state: Draft<IImportDocumentsState>) => {
            state.categorizationState = null;
        });
    },
};

export const getImportDocuments = createAsyncThunk('import/documents', thunks.getImportDocuments);
export const updateImportDocuments = createAsyncThunk(
    'import/documents/update',
    thunks.updateImportDocuments,
);

export const importDocumentsSlice = createSlice({
    name: 'importDocuments',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

export const { setDocuments, resetCategorizationState } = importDocumentsSlice.actions;

export default importDocumentsSlice.reducer;
